/**
 * Расстояния и своя позиция.
 *
 * Всё это жило приватно внутри pages/tasks.tsx, пока читателем был один экран.
 * Теперь их трое — задачи, карта и списки байков, — и копия в каждом означала бы
 * три расходящихся определения «далеко».
 *
 * Геопозиция берётся из navigator.geolocation, а не из @capacitor/geolocation,
 * хотя плагин в зависимостях есть. Переход на него — отдельное решение (план,
 * шаг К5): у плагина другая модель разрешений, и смешивать это с картой значит
 * отлаживать две вещи сразу.
 */
import { useEffect, useState } from 'react'
import type * as GeoJSON from 'geojson'

export type LatLng = { lat: number; lng: number }

/** Расстояние по большому кругу, метры. */
export function distanceM(a: LatLng, b: LatLng): number {
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

export function formatDistance(m: number): string {
  if (m < 1000) return `${Math.round(m)} м`
  return `${(m / 1000).toFixed(1)} км`
}

/**
 * Есть ли у объекта координаты вообще.
 *
 * Ровно (0, 0) — это не точка в Атлантике, а «неизвестно»: бэкенд опускает
 * нулевые поля, и оба нуля приезжают вместе именно тогда, когда координат нет.
 * Настоящий байк в Алматы даёт примерно (43.2, 76.9), так что ложное срабатывание
 * здесь стоило бы одного велосипеда в устье Гвинейского залива.
 */
export function hasPosition(p: { lat: number; lng: number }): boolean {
  return p.lat !== 0 || p.lng !== 0
}

/**
 * Своя позиция, пока экран открыт.
 *
 * Отказ в доступе — не ошибка: просто не будет расстояний. Экран обязан
 * работать и без неё, поэтому возвращается null, а не бросается исключение.
 */
export function useMyPosition(): LatLng | null {
  const [pos, setPos] = useState<LatLng | null>(null)
  useEffect(() => {
    if (!('geolocation' in navigator)) return
    const id = navigator.geolocation.watchPosition(
      (p) => {
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude })
      },
      () => {
        /* отказ в геолокации — просто не сортируем по расстоянию */
      },
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 15_000 },
    )
    return () => {
      navigator.geolocation.clearWatch(id)
    }
  }, [])
  return pos
}

/**
 * Круг радиусом в метрах как GeoJSON-полигон.
 *
 * Нужен хабам на карте: `radiusM` у точечной парковочной зоны — это и есть
 * ответ на вопрос «я уже внутри или ещё нет», а нарисовать его в mapbox
 * иначе нечем: `circle-radius` задаётся в пикселях и с зумом не связан.
 *
 * ⚠ ПРОЕКЦИЯ ЗДЕСЬ ПЛОСКАЯ, И ЭТО СОЗНАТЕЛЬНО. Метр долготы короче метра
 * широты в cos(широта) раз — это учтено; кривизной Земли на 20–500 метрах
 * пренебрегаем, ошибка меньше сантиметра. Для зоны катания в километрах так
 * считать было бы уже нельзя.
 *
 * Кольцо замыкается явно (последняя точка равна первой): без этого RFC 7946
 * не выполняется, и mapbox рисует незакрашенную дугу вместо круга.
 */
export function circlePolygon(
  center: LatLng,
  radiusM: number,
  steps = 48,
): GeoJSON.Polygon {
  const dLat = (radiusM / 111_320) * (180 / Math.PI) * (Math.PI / 180)
  // cos(90°) = 0 дал бы деление на ноль. На полюсах хабов нет, но пустой
  // слой лучше NaN, который mapbox проглотит молча.
  const cos = Math.cos((center.lat * Math.PI) / 180)
  const dLng = cos === 0 ? 0 : dLat / cos

  const ring: GeoJSON.Position[] = []
  for (let i = 0; i < steps; i++) {
    const angle = (i / steps) * 2 * Math.PI
    ring.push([center.lng + dLng * Math.cos(angle), center.lat + dLat * Math.sin(angle)])
  }
  ring.push(ring[0]!)

  return { type: 'Polygon', coordinates: [ring] }
}

/**
 * «3 ч назад» из RFC3339.
 *
 * Пустая строка — «не задано» (правило бэкенда), непарсящаяся — тоже: и в том
 * и в другом случае возвращается null, потому что «Invalid Date» на экране
 * хуже отсутствующей строки.
 *
 * Живёт рядом с расстояниями намеренно: и то и другое отвечает на вопрос
 * «насколько это далеко» — одно в метрах, другое во времени.
 */
export function timeAgo(rfc3339: string, now: Date = new Date()): string | null {
  if (rfc3339 === '') return null
  const then = new Date(rfc3339)
  if (Number.isNaN(then.getTime())) return null

  const min = Math.floor((now.getTime() - then.getTime()) / 60_000)
  // Часы сервера и телефона расходятся; «через 5 минут» у события в прошлом
  // читается как поломка, поэтому будущее схлопывается в «только что».
  if (min < 1) return 'только что'
  if (min < 60) return `${min} мин назад`

  const h = Math.floor(min / 60)
  if (h < 24) return `${h} ч назад`
  return `${Math.floor(h / 24)} дн назад`
}
